// ─────────────────────────────────────────────────────────────
// TradeEntry
// Extracted from App.js.
// ─────────────────────────────────────────────────────────────

import { useEffect, useState, useMemo } from "react";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { C } from "../../lib/colors";
import { parseCSV } from "../../lib/csv";
import { partitionTrades } from "../../lib/dedup";
import { getTickInfo } from "../../lib/instruments";
import { fmt$ } from "../../lib/format";
import { useUserRules } from "../../hooks/useUserRules";
import { evaluateAllRules, buildTradeContext } from "../../lib/rules";

// ─────────────────────────────────────────────────────────────
// InfoTooltip
// A small "?" badge that reveals a text bubble. Click-to-toggle
// (not hover-only) so the content is a real DOM element that can
// be screenshotted, selected, and stays open while the mouse moves
// away — unlike native `title` tooltips, which are OS-rendered and
// disappear the instant the cursor leaves the element.
// ─────────────────────────────────────────────────────────────
function InfoTooltip({ text }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    const closeOnOutsideClick = () => setOpen(false);
    // Slight delay so the opening click itself doesn't immediately close it
    const id = setTimeout(() => document.addEventListener("click", closeOnOutsideClick), 0);
    return () => {
      clearTimeout(id);
      document.removeEventListener("click", closeOnOutsideClick);
    };
  }, [open]);

  return (
    <span style={{ position: "relative", display: "inline-block", marginLeft: 2 }}>
      <span
        onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          width: 14,
          height: 14,
          borderRadius: "50%",
          border: `1px solid ${C.muted}`,
          color: C.muted,
          fontSize: 10,
          fontWeight: 700,
          cursor: "pointer",
          verticalAlign: "middle",
        }}
      >
        ?
      </span>
      {open && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "absolute",
            bottom: "150%",
            left: "50%",
            transform: "translateX(-50%)",
            width: 260,
            padding: "10px 12px",
            background: "#1a1d2e",
            border: `1px solid ${C.border}`,
            borderRadius: 8,
            color: C.text,
            fontSize: 11.5,
            lineHeight: 1.5,
            textAlign: "left",
            fontWeight: 400,
            boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
            zIndex: 50,
          }}
        >
          {text}
        </div>
      )}
    </span>
  );
}

// Local-time YYYY-MM-DD. Do NOT use new Date().toISOString().split("T")[0]
// for this — that gives UTC, which silently shifts to tomorrow's date for
// evening users in negative-offset timezones (Eastern, Central, etc).
function localTodayYMD() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function TradeEntry({ 
  addTrade, 
  updateTrade, 
  setView, 
  trades, 
  customStrategies = [], 
  strategyPreferences = { builtInEnabled: {} },
  accounts = [],
  currentAccountId,
  setCurrentAccountId,
  userId,
  supabase,
  isDemoAccount = false,
}) {
const [stage, setStage] = useState("account-select");

// State for capturing optional "what were you thinking?" reflection
// when a logged trade broke one or more rules.
const [deviationNote, setDeviationNote] = useState("");

// ─────────────────────────────────────────────────────────────
// CSV import preview state. When the user selects a CSV, we
// parse + partition first, then show a confirm dialog. Only on
// confirm do we actually insert trades.
// ─────────────────────────────────────────────────────────────
const [importPreview, setImportPreview] = useState(null);
const [importProgress, setImportProgress] = useState(null);
// importPreview shape:
//   { newTrades:  [...],   // trades not yet in DB
//     duplicates: [...],   // trades already present (will be skipped)
//     accountId:  "..."    // which account these will be imported into
//   }

const closeImportPreview = () => setImportPreview(null);

const confirmImport = async () => {
  if (!importPreview) return;
  if (isDemoAccount) { alert("Trade import is disabled in demo mode."); return; }
  const { newTrades, accountId } = importPreview;
  const total = newTrades.length;
  let imported = 0;

  // Initialize progress display
  setImportProgress({ total, current: 0, status: "importing", duplicates: importPreview.duplicates.length });

  try {
    for (const trade of newTrades) {
      await addTrade({ ...trade, account_id: accountId });
      imported++;
      // Update on every trade — React batches anyway so it's not expensive
      setImportProgress({ total, current: imported, status: "importing", duplicates: importPreview.duplicates.length });
    }
    // Show "done" state briefly before closing
    setImportProgress({ total, current: imported, status: "done", duplicates: importPreview.duplicates.length });
    setTimeout(() => {
      closeImportPreview();
      setImportProgress(null);
    }, 1500);
  } catch (err) {
    console.error("Import failed:", err);
    setImportProgress({ total, current: imported, status: "error", duplicates: importPreview.duplicates.length, error: String(err) });
  }
};

// CSV Import Function
const handleCSVImport = (e) => {
  const file = e.target.files[0];
  if (!file) return;

  const reader = new FileReader();
  reader.onload = async (event) => {
    const text = event.target.result;
    const parsed = parseCSV(text);

    if (parsed.length === 0) {
      alert("No valid trades found in the CSV file.");
      return;
    }

    if (!form.account_id) {
      alert("Please select an account in Stage 0 (Account Selection) before importing CSV.");
      return;
    }

    // Compare against the trades already in this account
    const existingInAccount = (trades || []).filter(
      t => t.account_id === form.account_id
    );
    const { newTrades, duplicates } = partitionTrades(parsed, existingInAccount);

    // Reset the file input so the same file can be re-picked later
    e.target.value = "";

    if (newTrades.length === 0) {
      alert(`No new trades to import. All ${parsed.length} trades in this CSV are already in your account.`);
      return;
    }

    // Show preview — user confirms before any DB write
    setImportPreview({
      newTrades,
      duplicates,
      accountId: form.account_id,
    });
  };
  reader.readAsText(file);
};
  const [editingTradeId, setEditingTradeId] = useState(null);

  const [form, setForm] = useState({
    symbol: "NQ",
    direction: "Long",
    trade_date: localTodayYMD(),
    entry_time: "",
    exit_time: "",
    entry_price: "",
    exit_price: "",
    position_size: "1",
    stop_loss: "",
    take_profit: "",
    commissions: "",
    fees: "",
    setup_type: "",
    notes: "",
    market_condition: "",
    entry_signal: "",
    conviction_level: 5,
    is_news_event: false,
    decision_speed: "moderate",
    mental_state: "",
    emotional_intensity: 5,
    focus_level: 5,
    rule_adherence: 5,
    impulsiveness: 5,
    energy_level: 5,
    market_context: 5,
    outcome_satisfaction: 5,
    account_id: ""
  });

  const [postTradeForm, setPostTradeForm] = useState({
    exit_reason: "",
    fear_level: 5,
    greed_level: 5,
    post_trade_emotion: "",
    post_trade_reflection: "",
    rule_violations: [],
    what_to_improve: ""
  });

  // ─────────────────────────────────────────────────────────────
  // Trading rules: evaluate against this trade's filled-in data.
  // We use the trade's account_id (which may differ from currentAccountId
  // if the user selected a different account in the form).
  // ─────────────────────────────────────────────────────────────
  const evaluationAccountId = form.account_id || currentAccountId;
  const { rules: userRules } = useUserRules(userId, evaluationAccountId);

  // Build a context from the form data. We need at least entry_time and
  // trade_date to evaluate timing rules. Pass prior same-day trades for
  // count-based rules (max_trades_per_day, max_losses_per_day).
  const ruleEvaluations = useMemo(() => {
    if (!userRules || userRules.length === 0) return [];
    if (!form.entry_time || !form.trade_date) return [];

    // Synthetic "current trade" object
    const draftTrade = {
      trade_date: form.trade_date,
      entry_time: form.entry_time,
      symbol: form.symbol,
      direction: form.direction,
      position_size: Number(form.position_size) || 1,
      pnl: 0,
      result: "Win", // placeholder; result not finalized yet
    };

    // Build context: include trades from the same account and day that
    // happened earlier than this one
    const ctx = buildTradeContext({
      trade: draftTrade,
      trades: (trades || []).filter(t => t.account_id === evaluationAccountId),
      news_events: [],
    });

    const enabled = userRules.filter(r => r.enabled);
    return evaluateAllRules(enabled, ctx);
  }, [userRules, form.entry_time, form.trade_date, form.symbol, form.direction, form.position_size, trades, evaluationAccountId]);

  const ruleViolations = useMemo(
    () => ruleEvaluations.filter(e => e.result?.violated),
    [ruleEvaluations]
  );

  // formatTime - Currently unused, keeping for potential future use
  // const formatTime = (input) => {
  //   if (!input) return "";
  //   let digits = input.replace(/[^0-9]/g, '').slice(0, 9);
  //   let result = '';
  //   if (digits.length >= 2) result += digits.slice(0,2) + ':';
  //   if (digits.length >= 4) result += digits.slice(2,4) + ':';
  //   if (digits.length >= 6) result += digits.slice(4,6);
  //   if (digits.length > 6) result += '.' + digits.slice(6,9);
  //   return result;
  // };

  // Load editing trade
  useEffect(() => {
    const editingId = sessionStorage.getItem('editingTradeId');
    const editingData = sessionStorage.getItem('editingTradeData');
    const editingStage = sessionStorage.getItem('editingStage');

    if (editingId && editingData) {
      try {
        const tradeData = JSON.parse(editingData);
        setEditingTradeId(editingId);
        setForm({
          symbol: tradeData.symbol || "NQ",
          direction: tradeData.direction || "Long",
          trade_date: tradeData.trade_date || localTodayYMD(),
          entry_time: tradeData.entry_time || "",
          exit_time: tradeData.exit_time || "",
          entry_price: tradeData.entry_price || "",
          exit_price: tradeData.exit_price || "",
          position_size: tradeData.position_size || "1",
          stop_loss: tradeData.stop_loss || "",
          take_profit: tradeData.take_profit || "",
          commissions: tradeData.commissions || "",
          fees: tradeData.fees || "",
          setup_type: tradeData.setup_type || "",
          notes: tradeData.notes || "",
          market_condition: tradeData.market_condition || "",
          entry_signal: tradeData.entry_signal || "",
          conviction_level: tradeData.conviction_level || 5,
          is_news_event: tradeData.is_news_event || false,
          decision_speed: tradeData.decision_speed || "moderate",
          mental_state: tradeData.mental_state || "",
          emotional_intensity: tradeData.emotional_intensity || 5
        });
        setPostTradeForm({
          exit_reason: tradeData.exit_reason || "",
          fear_level: tradeData.fear_level || 5,
          greed_level: tradeData.greed_level || 5,
          rule_violations: tradeData.rule_violations || [],
          post_trade_reflection: tradeData.post_trade_reflection || ""
        });
        setStage(editingStage || "pre-trade");
        sessionStorage.removeItem('editingTradeId');
        sessionStorage.removeItem('editingTradeData');
        sessionStorage.removeItem('editingStage');
      } catch (e) {
        console.error("Failed to load trade for editing", e);
      }
    }
  }, []);

  const builtInList = [
    { value: "Breakout_NewHigh", label: "Breakout - New High/Low" },
    { value: "Pullback_Support", label: "Pullback to Support/Resistance" },
    { value: "Reversal_Candle", label: "Reversal Candle (Pinbar, Engulfing)" },
    { value: "InsideBar_Breakout", label: "Inside Bar Breakout" },
    { value: "Flag_Pennant", label: "Bull/Bear Flag or Pennant" },
    { value: "Trend_Continuation", label: "Trend Continuation" },
    { value: "MA_Crossover", label: "Moving Average Crossover" },
    { value: "EMA_Ribbon", label: "EMA Ribbon Alignment" },
    { value: "Volume_Spike", label: "Volume Spike + Price Action" },
    { value: "RSI_Divergence", label: "RSI Divergence" },
    { value: "Bollinger_Squeeze", label: "Bollinger Band Squeeze" },
    { value: "VWAP_Reclaim", label: "VWAP Reclaim" },
    { value: "Scalp_Momentum", label: "Scalp - Momentum Ignition" },
    { value: "Opening_Range_Breakout", label: "Opening Range Breakout (ORB)" },
    { value: "News_Catalyst", label: "News / Economic Catalyst" },
    { value: "Confluence_Multiple", label: "Multiple Indicator Confluence" },
    { value: "OrderFlow_Delta", label: "Order Flow / Delta Divergence" },
    { value: "Mean_Reversion", label: "Mean Reversion Setup" },
  ];

  const riskReward = (() => {
    const entry = parseFloat(form.entry_price);
    const sl = parseFloat(form.stop_loss);
    const tp = parseFloat(form.take_profit);
    if (!entry || !sl || !tp) return "—";
    const risk = Math.abs(entry - sl);
    const reward = Math.abs(tp - entry);
    if (risk === 0) return "—";
    return `1:${(reward / risk).toFixed(2)}`;
  })();



  const calcPnl = () => {
    const e     = parseFloat(form.entry_price);
    const x     = parseFloat(form.exit_price);
    const size  = parseFloat(form.position_size) || 1;
    const fees  = parseFloat(form.fees)          || 0;
    const comms = parseFloat(form.commissions)   || 0;
    if (!e || !x) return 0;
    const dir = form.direction === "Long" ? 1 : -1;
    const { tickSize, tickValue } = getTickInfo(form.symbol, []);
    const ticks = (x - e) / tickSize;
    const gross = dir * ticks * tickValue * size;
    return Math.round((gross - fees - comms) * 100) / 100;
  };

  const estPnl = calcPnl();

  const canProceed = () => {
    if (stage === "pre-trade") return form.market_condition && form.entry_signal;
    if (stage === "trade-entry") return form.entry_price && form.position_size;
    if (stage === "post-trade") return true;
    return false;
  };

  const handleNext = async () => {
    if (!canProceed()) return alert("Please complete required fields for this stage.");

    if (stage === "pre-trade") setStage("trade-entry");
    else if (stage === "trade-entry") setStage("post-trade");
    else {
      // ✅ Calculate duration_minutes from entry_time and exit_time
      let duration = null;
      if (form.entry_time && form.exit_time) {
        try {
          // Parse times in HH:MM:SS.mmm or HH:MM format
          const parseTime = (timeStr) => {
            if (!timeStr) return null;
            // Remove any whitespace
            timeStr = timeStr.trim();
            const parts = timeStr.split(':');
            if (parts.length < 2) return null;
            
            const hours = parseInt(parts[0], 10);
            const minutes = parseInt(parts[1], 10);
            
            // Handle seconds with or without milliseconds (HH:MM:SS.mmm or HH:MM:SS)
            let seconds = 0;
            if (parts.length >= 3) {
              const secondParts = parts[2].split('.');
              seconds = parseInt(secondParts[0], 10) || 0;
            }
            
            if (isNaN(hours) || isNaN(minutes) || isNaN(seconds)) return null;
            
            return hours * 3600 + minutes * 60 + seconds;
          };
          
          const entrySeconds = parseTime(form.entry_time);
          const exitSeconds = parseTime(form.exit_time);
          
          console.log(`Duration calc: entry=${form.entry_time} (${entrySeconds}s), exit=${form.exit_time} (${exitSeconds}s)`);
          
          if (entrySeconds !== null && exitSeconds !== null) {
            let diffSeconds = exitSeconds - entrySeconds;
            
            // Handle case where trade spans multiple days (exit time < entry time)
            if (diffSeconds < 0) {
              diffSeconds += 24 * 3600; // Add 24 hours
            }
            
            duration = Math.round(diffSeconds / 60); // Convert to minutes
            console.log(`Duration calculated: ${duration} minutes`);
          }
        } catch (e) {
          console.error("Error calculating duration:", e);
        }
      }

     // Build rule_deviations payload from the violations we detected
     const ruleDeviations = ruleViolations.map(v => ({
       rule_id: v.rule.id,
       rule_type: v.rule.rule_type,
       label: v.rule.custom_label || v.def?.label || v.rule.rule_type,
       reason: v.result?.reason || "",
     }));

     const finalTrade = {
  ...form,
  ...postTradeForm,
  account_id: form.account_id || currentAccountId,
  pnl: estPnl,
  risk_reward: riskReward !== "—" ? riskReward : null,
  result: estPnl > 0 ? "Win" : estPnl < 0 ? "Loss" : "Breakeven",
  duration_minutes: duration,
  // Ensure mental_state is set only once (from form)
  mental_state: form.mental_state || postTradeForm.mental_state || null,
  // Rule deviations: which rules this trade broke (computed at log time)
  rule_deviations: ruleDeviations,
  rule_deviation_note: deviationNote.trim() || null,
  // Map entry_signal → setup_type so By Setup analytics and Context detectors
  // work correctly for manually entered trades.
  setup_type: form.entry_signal || form.setup_type || null,
};

      console.log("Final trade object:", finalTrade);

      if (editingTradeId) {
        await updateTrade(editingTradeId, finalTrade);
        alert("✅ Trade Updated Successfully!");
      } else {
        if (isDemoAccount) { alert("Trade logging is disabled in demo mode."); return; }
        await addTrade(finalTrade);
        alert("✅ Trade Saved Successfully!");
      }

      setView("journal");
      setStage("pre-trade");
      setEditingTradeId(null);
      setDeviationNote("");
    }
  };

  return (
    <div style={{ maxWidth: 720, margin: "0 auto", padding: 20 }}>
      <button onClick={() => setView("dashboard")} style={{ color: C.muted }}>← Dashboard</button>
      <h2>Log Trade — 3-Stage</h2>

      {isDemoAccount && (
        <div style={{
          margin: "24px 0",
          padding: "32px 24px",
          background: "#1a1500",
          border: "1px solid #f5a62330",
          borderRadius: 12,
          textAlign: "center",
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>👀</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "#f5a623", marginBottom: 8 }}>
            Demo Mode
          </div>
          <div style={{ fontSize: 13, color: "#aaa", lineHeight: 1.7, maxWidth: 360, margin: "0 auto" }}>
            Trade logging is disabled in the demo. Sign up for a free 30-day trial to start logging your own trades and unlock behavioral analysis.
          </div>
        </div>
      )}

<div style={{ display: "flex", gap: 8, margin: "30px 0" }}>
  {["account-select", "pre-trade", "trade-entry", "post-trade"].map((s, i) => (
    <div key={s} style={{
      flex: 1, padding: "14px", borderRadius: 10, textAlign: "center",
      background: stage === s ? C.blue + "30" : "#1a1d2e",
      border: `2px solid ${stage === s ? C.blue : C.border}`,
      color: stage === s ? C.blue : C.muted
    }}>
      {i + 1}. {s === "account-select" ? "Account" :
               s === "pre-trade" ? "Pre-Trade" :
               s === "trade-entry" ? "Execution" : "Reflection"}
    </div>
  ))}
</div>


{/* ACCOUNT SELECTION STAGE - FIRST STEP */}
{stage === "account-select" && (
  <Card>
    <h3>0. Select Trading Account</h3>
    <p style={{ color: C.muted, marginBottom: 20 }}>All trades in this session will be linked to this account.</p>
    
    <select
      value={form.account_id || ""}
      onChange={e => {
        const accId = e.target.value;
        setForm(p => ({...p, account_id: accId}));
        setCurrentAccountId(accId);   // Sync global state
      }}
      style={{ width: "100%", padding: 16, background: "#1a1d2e", color: "#ffffff", border: `2px solid ${C.border}`, borderRadius: 12, fontSize: 15 }}
    >
      <option value="">Choose an Account...</option>
      {accounts && accounts.length > 0 ? (
        accounts.map(acc => (
          <option key={acc.id} value={acc.id}>
            {acc.prop_firm ? `${acc.prop_firm} — ` : ""}{acc.name} — {acc.account_type?.toUpperCase()} — ${acc.starting_balance}
          </option>
        ))
      ) : (
        <option value="">No accounts found — go to Portfolio first</option>
      )}
    </select>

    {/* Prop firm context — shown once an account is selected */}
    {form.account_id && (() => {
      const selectedAcc = accounts.find(a => a.id === form.account_id);
      if (!selectedAcc) return null;
      return (
        <div style={{
          marginTop: 14,
          padding: "12px 16px",
          background: "#1a1d2e",
          border: `1px solid ${C.border}`,
          borderRadius: 10,
          display: "flex",
          flexWrap: "wrap",
          gap: 16,
        }}>
          {selectedAcc.prop_firm && (
            <div>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>Prop Firm</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.blue }}>{selectedAcc.prop_firm}</div>
            </div>
          )}
          <div>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>Account Type</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{selectedAcc.account_type?.toUpperCase()}</div>
          </div>
          <div>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>Balance</div>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>${Number(selectedAcc.starting_balance).toLocaleString()}</div>
          </div>
          {selectedAcc.payout_split && (
            <div>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>Your Split</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.green }}>{Math.round(selectedAcc.payout_split * 100)}%</div>
            </div>
          )}
          {selectedAcc.broker && (
            <div>
              <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 3 }}>Platform</div>
              <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{selectedAcc.broker}</div>
            </div>
          )}
        </div>
      );
    })()}

    <Btn
      onClick={() => {
        if (form.account_id) {
          setCurrentAccountId(form.account_id);   // Ensure it's set
          setStage("pre-trade");
        } else {
          alert("Please select an account");
        }
      }}
      style={{ width: "100%", marginTop: 24 }}
    >
      Continue to Pre-Trade →
    </Btn>
  </Card>
)}




      {/* PRE-TRADE STAGE */}
      {stage === "pre-trade" && (
        <Card>
          <h3>1. Pre-Trade</h3>

          {/* CSV Import Button */}
          <div style={{ marginBottom: 24, textAlign: "center" }}>
            <label style={{ 
              display: "inline-block", 
              padding: "12px 24px", 
              background: C.blue + "15", 
              border: `2px solid ${C.blue}`, 
              borderRadius: 12, 
              color: C.blue, 
              cursor: "pointer", 
              fontSize: 14,
              fontWeight: 600
            }}>
              📥 Import CSV (TopStep, Tradovate, etc.)
              <input 
                type="file" 
                accept=".csv" 
                onChange={handleCSVImport} 
                style={{ display: "none" }} 
              />
            </label>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
              Supports TopStep / Tradovate / most broker exports
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
              Tradovate: export from the <span style={{ color: C.text, fontWeight: 600 }}>Fills</span> tab, not Orders
              {" "}
              <InfoTooltip text={'Tradovate caps how wide a date range you can export at once. If you see "Too long range," split your export into smaller date chunks (e.g. one month at a time) and upload each CSV — new trades will be merged in and duplicates skipped automatically.'} />
            </div>
          </div>



          <div style={{ marginBottom: 24 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#ffffff", display: "block", marginBottom: 12 }}>Market Condition</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {["Trending Up", "Trending Down", "Ranging", "Volatile"].map(cond => (
                <button key={cond} onClick={() => setForm(p => ({...p, market_condition: cond}))}
                  style={{
                    padding: "14px 12px",
                    borderRadius: 8,
                    border: form.market_condition === cond ? `2px solid ${C.green}` : `1px solid ${C.border}`,
                    background: form.market_condition === cond ? C.green + "20" : "#1a1d2e",
                    color: form.market_condition === cond ? C.green : "#ffffff",
                    fontWeight: 600,
                    cursor: "pointer"
                  }}>
                  {cond.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

           <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#ffffff", display: "block", marginBottom: 8 }}>Entry Signal / Strategy</label>
            <select value={form.entry_signal} onChange={e => setForm(p => ({...p, entry_signal: e.target.value}))}
              style={{ width: "100%", padding: "12px", background: "#1a1d2e", border: `1px solid ${C.border}`, borderRadius: 8, color: "#ffffff" }}>
              <option value="">Select Strategy / Pattern...</option>
              {builtInList
                .filter(s => strategyPreferences.builtInEnabled[s.value] !== false)
                .map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
              {customStrategies && customStrategies.length > 0 && (
                <optgroup label="My Custom Strategies">
                  {customStrategies.filter(s => s.enabled !== false).map(strat => (
                    <option key={strat.id || strat.name} value={strat.name}>{strat.name}</option>
                  ))}
                </optgroup>
              )}
            </select>
          </div>

   

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 600, color: "#ffffff", display: "block", marginBottom: 8 }}>
              Conviction Level: <span style={{ color: C.blue }}>{form.conviction_level}/10</span>
            </label>
            <input type="range" min="1" max="10" value={form.conviction_level} onChange={e => setForm(p => ({...p, conviction_level: +e.target.value}))} style={{width:"100%", accentColor: C.blue}} />
          </div>

          <div style={{ marginBottom: 24, padding: "12px", background: "#1a1d2e", borderRadius: 8, border: `1px solid ${C.border}` }}>
            <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
              <input type="checkbox" checked={form.is_news_event} onChange={e => setForm(p => ({...p, is_news_event: e.target.checked}))} style={{ width: 18, height: 18, accentColor: C.orange }} />
              <span style={{ color: "#ffffff", fontSize: 14 }}>Trade during or around a significant news event</span>
            </label>
          </div>

          <Btn onClick={handleNext} disabled={!canProceed()} style={{ width: "100%", padding: "14px" }}>
            Next → Execution
          </Btn>
        </Card>
      )}






      {/* EXECUTION STAGE - ALL FIELDS BRIGHT & EDITABLE */}
      {stage === "trade-entry" && (
        <Card>
          <h3>2. Execution</h3>

          <div style={{ marginTop: 16 }}>
            <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>Symbol</label>
            <input 
              type="text" 
              value={form.symbol} 
              onChange={e => setForm(p => ({...p, symbol: e.target.value.toUpperCase()}))} 
              style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#ffffff", border: `1px solid ${C.border}`, borderRadius: 8 }} 
            />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Trade Date</label>
              <input 
                type="date" 
                value={form.trade_date} 
                onChange={e => setForm(p => ({...p, trade_date: e.target.value}))} 
                style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#e0e0e0", borderRadius: 8 }} 
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Position Size</label>
              <input 
                type="number" 
                value={form.position_size} 
                onChange={e => setForm(p => ({...p, position_size: e.target.value}))} 
                style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#e0e0e0", borderRadius: 8 }} 
              />
            </div>
          </div>

        {/* TIME FIELDS - FIXED */}
<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
  <div>
    <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Entry Time</label>
    <input 
      type="text" 
      placeholder="HH:MM:SS.mmm" 
      maxLength="12"
      value={form.entry_time || ""} 
      onChange={e => {
        let val = e.target.value.replace(/[^0-9:.]/g, '');
        
        // Auto-format as user types
        if (val.length === 2 && !val.includes(':')) {
          val = val + ':';
        } else if (val.length === 5 && (val.match(/:/g) || []).length === 1) {
          val = val + ':';
        } else if (val.length === 8 && (val.match(/:/g) || []).length === 2) {
          val = val + '.';
        }
        
        // Prevent exceeding max length
        if (val.length > 12) {
          val = val.slice(0, 12);
        }
        
        setForm(p => ({...p, entry_time: val}));
      }}
      style={{ 
        width: "100%", 
        padding: 14, 
        background: "#1a1d2e", 
        color: "#e0e0e0", 
        borderRadius: 8, 
        fontFamily: "monospace", 
        fontSize: 16 
      }} 
    />
  </div>
  <div>
    <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Exit Time</label>
    <input 
      type="text" 
      placeholder="HH:MM:SS.mmm" 
      maxLength="12"
      value={form.exit_time || ""} 
      onChange={e => {
        let val = e.target.value.replace(/[^0-9:.]/g, '');
        
        // Enforce format: HH:MM:SS.mmm
        if (val.length === 2 && !val.includes(':')) {
          val = val + ':';
        } else if (val.length === 5 && val.match(/^\d{2}:\d{2}$/)) {
          val = val + ':';
        } else if (val.length === 8 && val.match(/^\d{2}:\d{2}:\d{2}$/)) {
          val = val + '.';
        } else if (val.length > 12) {
          val = val.slice(0, 12);
        }
        
        // Validate each part
        const parts = val.split(/[:.]/)
        if (parts[0] && parts[0].length === 2) {
          const hh = parseInt(parts[0]);
          if (hh > 23) val = '23' + val.substring(2);
        }
        if (parts[1] && parts[1].length === 2) {
          const mm = parseInt(parts[1]);
          if (mm > 59) val = val.substring(0, 3) + '59' + val.substring(5);
        }
        if (parts[2] && parts[2].length === 2) {
          const ss = parseInt(parts[2]);
          if (ss > 59) val = val.substring(0, 6) + '59' + val.substring(8);
        }
        if (parts[3] && parts[3].length === 3) {
          const mmm = parseInt(parts[3]);
          if (mmm > 999) val = val.substring(0, 9) + '999';
        }
        
        setForm(p => ({...p, exit_time: val}));
      }}
      style={{ 
        width: "100%", 
        padding: 14, 
        background: "#1a1d2e", 
        color: "#e0e0e0", 
        borderRadius: 8, 
        fontFamily: "monospace", 
        fontSize: 16 
      }} 
    />
  </div>
</div>

          {/* DIRECTION TOGGLE */}
          <div style={{ marginTop: 20, marginBottom: 20 }}>
            <label style={{ display: "block", marginBottom: 12, color: "#ffffff", fontSize: 14, fontWeight: 600 }}>Trade Direction *</label>
            <div style={{ display: "flex", gap: 12 }}>
              <button
                onClick={() => setForm(p => ({...p, direction: "Long"}))}
                style={{
                  flex: 1,
                  padding: 14,
                  background: form.direction === "Long" ? "#00e676" : "#1a1d2e",
                  color: form.direction === "Long" ? "#000" : "#00e676",
                  border: `2px solid ${form.direction === "Long" ? "#00e676" : "#00e67640"}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 700,
                  transition: "all 0.2s"
                }}
              >
                ↑ Long
              </button>
              <button
                onClick={() => setForm(p => ({...p, direction: "Short"}))}
                style={{
                  flex: 1,
                  padding: 14,
                  background: form.direction === "Short" ? "#ff1744" : "#1a1d2e",
                  color: form.direction === "Short" ? "#fff" : "#ff1744",
                  border: `2px solid ${form.direction === "Short" ? "#ff1744" : "#ff174440"}`,
                  borderRadius: 8,
                  cursor: "pointer",
                  fontSize: 14,
                  fontWeight: 700,
                  transition: "all 0.2s"
                }}
              >
                ↓ Short
              </button>
            </div>
          </div>

          {/* PRICE FIELDS */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Entry Price *</label>
              <input 
                type="number" 
                step="0.01" 
                value={form.entry_price || ""} 
                onChange={e => setForm(p => ({...p, entry_price: e.target.value}))} 
                style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#e0e0e0", borderRadius: 8 }} 
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Exit Price *</label>
              <input 
                type="number" 
                step="0.01" 
                value={form.exit_price || ""} 
                onChange={e => setForm(p => ({...p, exit_price: e.target.value}))} 
                style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#e0e0e0", borderRadius: 8 }} 
              />
            </div>
          </div>

          {/* RISK FIELDS */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Stop Loss</label>
              <input 
                type="number" 
                step="0.01" 
                value={form.stop_loss || ""} 
                onChange={e => setForm(p => ({...p, stop_loss: e.target.value}))} 
                style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#e0e0e0", borderRadius: 8 }} 
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Take Profit</label>
              <input 
                type="number" 
                step="0.01" 
                value={form.take_profit || ""} 
                onChange={e => setForm(p => ({...p, take_profit: e.target.value}))} 
                style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#e0e0e0", borderRadius: 8 }} 
              />
            </div>
          </div>

          {/* RISK : REWARD */}
          <div style={{ marginTop: 16, textAlign: "center", fontSize: 13, color: C.muted }}>
            Risk : Reward — {riskReward}
          </div>

          {/* COMMISSIONS & FEES */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginTop: 20 }}>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Commissions</label>
              <input 
                type="number" 
                step="0.01" 
                value={form.commissions || ""} 
                onChange={e => setForm(p => ({...p, commissions: e.target.value}))} 
                style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#e0e0e0", borderRadius: 8 }} 
              />
            </div>
            <div>
              <label style={{ display: "block", marginBottom: 8, color: "#ffffff" }}>Fees</label>
              <input 
                type="number" 
                step="0.01" 
                value={form.fees || ""} 
                onChange={e => setForm(p => ({...p, fees: e.target.value}))} 
                style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#e0e0e0", borderRadius: 8 }} 
              />
            </div>
          </div>

          {estPnl !== 0 && (
            <div style={{ margin: "24px 0", padding: 14, background: estPnl > 0 ? C.green + "20" : C.red + "20", color: estPnl > 0 ? C.green : C.red, borderRadius: 8, textAlign: "center", fontWeight: 700 }}>
              Estimated P&L: {fmt$(estPnl)}
            </div>
          )}

          <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
            <Btn onClick={() => setStage("pre-trade")}>← Back</Btn>
            <Btn onClick={handleNext} disabled={!canProceed()}>Next → Reflection</Btn>
          </div>
        </Card>
      )}





      {/* POST-TRADE STAGE - Enhanced Reflection */}
      {stage === "post-trade" && (
        <Card>
          <h3>3. Post-Trade Reflection</h3>

          {/* Rule Deviation Banner — shown if this trade broke any active rules */}
          {ruleViolations.length > 0 && (
            <RuleDeviationBanner
              violations={ruleViolations}
              note={deviationNote}
              setNote={setDeviationNote}
            />
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>

            {/* Left Column - Sliders */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>
                  Pre-Trade Confidence: <span style={{ color: C.blue }}>{form.confidence_level || 5}/10</span>
                </label>
                <input type="range" min="1" max="10" value={form.confidence_level || 5} onChange={e => setForm(p => ({...p, confidence_level: +e.target.value}))} style={{width:"100%", accentColor: C.blue}} />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>
                  Focus Level: <span style={{ color: C.blue }}>{form.focus_level || 5}/10</span>
                </label>
                <input type="range" min="1" max="10" value={form.focus_level || 5} onChange={e => setForm(p => ({...p, focus_level: +e.target.value}))} style={{width:"100%", accentColor: C.blue}} />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>
                  Rule Adherence: <span style={{ color: C.blue }}>{form.rule_adherence || 5}/10</span>
                </label>
                <input type="range" min="1" max="10" value={form.rule_adherence || 5} onChange={e => setForm(p => ({...p, rule_adherence: +e.target.value}))} style={{width:"100%", accentColor: C.blue}} />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>
                  Impulsiveness: <span style={{ color: C.red }}>{form.impulsiveness || 5}/10</span>
                </label>
                <input type="range" min="1" max="10" value={form.impulsiveness || 5} onChange={e => setForm(p => ({...p, impulsiveness: +e.target.value}))} style={{width:"100%", accentColor: C.red}} />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>
                  Energy Level: <span style={{ color: C.yellow }}>{form.energy_level || 5}/10</span>
                </label>
                <input type="range" min="1" max="10" value={form.energy_level || 5} onChange={e => setForm(p => ({...p, energy_level: +e.target.value}))} style={{width:"100%", accentColor: C.yellow}} />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>
                  Market Context Awareness: <span style={{ color: C.blue }}>{form.market_context || 5}/10</span>
                </label>
                <input type="range" min="1" max="10" value={form.market_context || 5} onChange={e => setForm(p => ({...p, market_context: +e.target.value}))} style={{width:"100%", accentColor: C.blue}} />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>
                  Trade Outcome Satisfaction: <span style={{ color: C.green }}>{form.outcome_satisfaction || 5}/10</span>
                </label>
                <input type="range" min="1" max="10" value={form.outcome_satisfaction || 5} onChange={e => setForm(p => ({...p, outcome_satisfaction: +e.target.value}))} style={{width:"100%", accentColor: C.green}} />
              </div>

              {/* Fear & Greed Sliders */}
              <div>
                <label style={{ display: "block", marginBottom: 8, color: C.red, fontWeight: 600 }}>
                  Fear Level: {postTradeForm.fear_level || 5}/10
                </label>
                <input type="range" min="1" max="10" value={postTradeForm.fear_level || 5} onChange={e => setPostTradeForm(p => ({...p, fear_level: +e.target.value}))} style={{width:"100%", accentColor: C.red}} />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: C.green, fontWeight: 600 }}>
                  Greed Level: {postTradeForm.greed_level || 5}/10
                </label>
                <input type="range" min="1" max="10" value={postTradeForm.greed_level || 5} onChange={e => setPostTradeForm(p => ({...p, greed_level: +e.target.value}))} style={{width:"100%", accentColor: C.green}} />
              </div>
            </div>

            {/* Right Column */}
            <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>Pre-Trade Emotional State</label>
                <select value={form.mental_state || ""} onChange={e => setForm(p => ({...p, mental_state: e.target.value}))} style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#ffffff", borderRadius: 8 }}>
                  <option value="">Select...</option>
                  <option value="Calm">Calm & Focused</option>
                  <option value="Anxious">Anxious / Hesitant</option>
                  <option value="Excited">Excited / Eager</option>
                  <option value="Frustrated">Frustrated / Tilted</option>
                  <option value="Overconfident">Overconfident</option>
                  <option value="Tired">Tired / Low Energy</option>
                  <option value="FOMO">FOMO</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>Exit Reason</label>
                <select value={postTradeForm.exit_reason || ""} onChange={e => setPostTradeForm(p => ({...p, exit_reason: e.target.value}))} style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#ffffff", borderRadius: 8 }}>
                  <option value="">Select exit reason...</option>
                  <option value="target_hit">Target Hit</option>
                  <option value="stop_hit">Stop Hit</option>
                  <option value="manual_exit">Manual Exit</option>
                  <option value="time_exit">Time Exit</option>
                  <option value="breakeven">Breakeven</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>Post-Trade Emotion</label>
                <select value={postTradeForm.post_trade_emotion || ""} onChange={e => setPostTradeForm(p => ({...p, post_trade_emotion: e.target.value}))} style={{ width: "100%", padding: 14, background: "#1a1d2e", color: "#ffffff", borderRadius: 8 }}>
                  <option value="">Select...</option>
                  <option value="Proud">Proud / Satisfied</option>
                  <option value="Relieved">Relieved</option>
                  <option value="Regretful">Regretful / Disappointed</option>
                  <option value="Angry">Angry at myself</option>
                  <option value="Euphoric">Euphoric (dangerous)</option>
                  <option value="Numb">Numb</option>
                  <option value="Indifferent">Indifferent</option>
                </select>
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>What did I learn from this trade?</label>
                <textarea 
                  value={postTradeForm.post_trade_reflection} 
                  onChange={e => setPostTradeForm(p => ({...p, post_trade_reflection: e.target.value}))} 
                  placeholder="Key takeaway..." 
                  style={{ width: "100%", minHeight: 90, padding: 14, background: "#1a1d2e", color: "#ffffff", borderRadius: 8 }}
                />
              </div>

              <div>
                <label style={{ display: "block", marginBottom: 8, color: "#ffffff", fontWeight: 600 }}>What would I do differently next time?</label>
                <textarea 
                  value={postTradeForm.what_to_improve || ""} 
                  onChange={e => setPostTradeForm(p => ({...p, what_to_improve: e.target.value}))} 
                  placeholder="Be specific..." 
                  style={{ width: "100%", minHeight: 90, padding: 14, background: "#1a1d2e", color: "#ffffff", borderRadius: 8 }}
                />
              </div>
            </div>
          </div>

          <div style={{ display: "flex", gap: 12, marginTop: 32 }}>
            <Btn onClick={() => setStage("trade-entry")}>← Back</Btn>
            <Btn onClick={handleNext}>✓ Save Trade</Btn>
          </div>
        </Card>
      )}

      {/* CSV Import Preview Modal */}
      {importPreview && (
        <ImportPreviewModal
          preview={importPreview}
          progress={importProgress}
          onCancel={closeImportPreview}
          onConfirm={confirmImport}
        />
      )}

    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ImportPreviewModal
// Shows "X new, Y duplicates" before any DB writes happen.
// ─────────────────────────────────────────────────────────────
function ImportPreviewModal({ preview, progress, onCancel, onConfirm }) {
  const { newTrades, duplicates } = preview;
  const total = newTrades.length + duplicates.length;
  const [submitting, setSubmitting] = useState(false);

  const handleConfirm = async () => {
    setSubmitting(true);
    try { await onConfirm(); }
    finally { setSubmitting(false); }
  };

  // If progress is active, replace the body with a progress bar.
  const isImporting = progress && progress.status === "importing";
  const isDone      = progress && progress.status === "done";
  const isError     = progress && progress.status === "error";
  const pct = progress && progress.total > 0
    ? Math.round((progress.current / progress.total) * 100)
    : 0;

  return (
    <div style={{
      position: "fixed",
      inset: 0,
      background: "rgba(0,0,0,0.7)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 100,
      padding: 16,
    }}>
      <div style={{
        background: C.panel,
        border: `1px solid ${C.border}`,
        borderRadius: 12,
        padding: 24,
        maxWidth: 480,
        width: "100%",
        maxHeight: "90vh",
        overflowY: "auto",
      }}>
        {progress ? (
          <ImportProgressView
            progress={progress}
            pct={pct}
            isImporting={isImporting}
            isDone={isDone}
            isError={isError}
          />
        ) : (
          <>
        <h2 style={{ marginBottom: 8, fontSize: 18, color: C.text }}>
          Import Preview
        </h2>
        <p style={{ fontSize: 13, color: C.muted, marginBottom: 18, lineHeight: 1.5 }}>
          Found <strong style={{ color: C.text }}>{total}</strong> trades in your CSV.
        </p>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 18 }}>
          <div style={{
            padding: 14,
            background: C.green + "15",
            border: `1px solid ${C.green}40`,
            borderRadius: 8,
            textAlign: "center",
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: C.green, fontFamily: "monospace" }}>
              {newTrades.length}
            </div>
            <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              New
            </div>
          </div>
          <div style={{
            padding: 14,
            background: C.muted + "15",
            border: `1px solid ${C.muted}40`,
            borderRadius: 8,
            textAlign: "center",
          }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: C.muted, fontFamily: "monospace" }}>
              {duplicates.length}
            </div>
            <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Already imported
            </div>
          </div>
        </div>

        {newTrades.length > 0 && (
          <div style={{ marginBottom: 18 }}>
            <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
              Preview of new trades (first 5)
            </div>
            <div style={{
              background: C.border + "30",
              borderRadius: 6,
              padding: 10,
              fontSize: 11,
              fontFamily: "monospace",
              color: C.sub,
            }}>
              {newTrades.slice(0, 5).map((t, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "2px 0" }}>
                  <span>{t.trade_date} {t.symbol} {t.direction}</span>
                  <span style={{ color: t.pnl >= 0 ? C.green : C.red }}>
                    {t.pnl >= 0 ? "+" : ""}{Number(t.pnl).toFixed(2)}
                  </span>
                </div>
              ))}
              {newTrades.length > 5 && (
                <div style={{ color: C.muted, paddingTop: 4 }}>
                  …and {newTrades.length - 5} more
                </div>
              )}
            </div>
          </div>
        )}

        <p style={{ fontSize: 12, color: C.muted, marginBottom: 18, lineHeight: 1.5 }}>
          {duplicates.length > 0
            ? `${duplicates.length} duplicates will be skipped. Only the ${newTrades.length} new trades will be added.`
            : `All ${newTrades.length} trades are new. None are duplicates.`}
        </p>

        <div className="fos-btn-row" style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button
            onClick={onCancel}
            disabled={submitting}
            style={{
              padding: "10px 18px",
              background: "transparent",
              border: `1px solid ${C.border}`,
              borderRadius: 6,
              color: C.text,
              cursor: submitting ? "not-allowed" : "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={submitting || newTrades.length === 0}
            style={{
              padding: "10px 18px",
              background: C.green,
              border: "none",
              borderRadius: 6,
              color: "#000",
              cursor: (submitting || newTrades.length === 0) ? "not-allowed" : "pointer",
              fontWeight: 700,
              fontSize: 13,
              opacity: (submitting || newTrades.length === 0) ? 0.5 : 1,
            }}
          >
            {submitting ? "Importing…" : `Import ${newTrades.length} new trades`}
          </button>
        </div>
          </>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// ImportProgressView
// Shown inside ImportPreviewModal while trades are being inserted.
// Replaces the whole modal body once the user clicks "Import".
// ─────────────────────────────────────────────────────────────
function ImportProgressView({ progress, pct, isImporting, isDone, isError }) {
  const { total, current, duplicates, error } = progress;

  // Color logic for the bar
  const barColor = isError ? C.red : isDone ? C.green : C.blue;

  return (
    <div>
      <h2 style={{ marginBottom: 8, fontSize: 18, color: C.text }}>
        {isDone ? "✓ Import Complete" : isError ? "Import Failed" : "Importing trades…"}
      </h2>
      <p style={{ fontSize: 13, color: C.muted, marginBottom: 22, lineHeight: 1.5 }}>
        {isDone
          ? `Successfully added ${current} trade${current === 1 ? "" : "s"} to this account.`
          : isError
          ? "Something went wrong. Some trades may have been partially imported."
          : `Adding trade ${current} of ${total} to your account…`}
      </p>

      {/* Progress bar */}
      <div style={{
        background: "#1a1d2e",
        border: `1px solid ${C.border}`,
        borderRadius: 8,
        height: 14,
        overflow: "hidden",
        marginBottom: 10,
      }}>
        <div style={{
          width: `${pct}%`,
          height: "100%",
          background: barColor,
          transition: "width 0.2s ease-out",
        }} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 20 }}>
        <span style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>
          {current} / {total}
        </span>
        <span style={{ fontSize: 22, fontWeight: 800, color: barColor, fontFamily: "monospace" }}>
          {pct}%
        </span>
      </div>

      {/* Skipped duplicates footnote */}
      {duplicates > 0 && (
        <div style={{
          padding: "10px 12px",
          background: C.muted + "11",
          border: `1px solid ${C.muted}33`,
          borderRadius: 6,
          fontSize: 11,
          color: C.muted,
          lineHeight: 1.5,
          marginBottom: 14,
        }}>
          {duplicates} duplicate{duplicates === 1 ? "" : "s"} skipped (already in your account).
        </div>
      )}

      {/* Error display */}
      {isError && error && (
        <div style={{
          padding: "10px 12px",
          background: C.red + "15",
          border: `1px solid ${C.red}55`,
          borderRadius: 6,
          fontSize: 11,
          color: C.red,
          fontFamily: "monospace",
          wordBreak: "break-word",
        }}>
          {error}
        </div>
      )}

      {/* Spinner during active import */}
      {isImporting && (
        <div style={{ textAlign: "center", marginTop: 8 }}>
          <span style={{
            display: "inline-block",
            width: 18,
            height: 18,
            border: `2px solid ${C.border}`,
            borderTopColor: C.blue,
            borderRadius: "50%",
            animation: "fos-spin 0.8s linear infinite",
            verticalAlign: "middle",
            marginRight: 6,
          }} />
          <span style={{ fontSize: 11, color: C.muted, verticalAlign: "middle" }}>
            Please don't close this window
          </span>
        </div>
      )}

      {/* Keyframes for spinner — scoped inline */}
      <style>{`
        @keyframes fos-spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}






// ─────────────────────────────────────────────────────────────
// RuleDeviationBanner
// Shown in Stage 3 (Post-Trade Reflection) when the trade being
// logged broke one or more of the user's active rules.
// Past-tense, journaling tone — never blocks the trade.
// ─────────────────────────────────────────────────────────────
function RuleDeviationBanner({ violations, note, setNote }) {
  return (
    <div style={{
      marginBottom: 20,
      padding: "16px 18px",
      background: C.yellow + "11",
      border: `1px solid ${C.yellow}55`,
      borderRadius: 10,
    }}>
      <div style={{
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
        marginBottom: 12,
      }}>
        <div style={{ fontSize: 20, lineHeight: 1 }}>⚠</div>
        <div>
          <div style={{
            fontSize: 14,
            fontWeight: 700,
            color: C.yellow,
            marginBottom: 4,
          }}>
            This trade broke {violations.length} of your rule{violations.length === 1 ? "" : "s"}
          </div>
          <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>
            Just a reflection — the trade is saved either way. We'll track this in your
            analytics so you can see patterns over time.
          </div>
        </div>
      </div>

      <ul style={{
        margin: "0 0 14px 0",
        paddingLeft: 28,
        color: C.text,
        fontSize: 12.5,
        lineHeight: 1.7,
      }}>
        {violations.map(v => (
          <li key={v.rule.id}>
            <strong style={{ color: C.text }}>
              {v.rule.custom_label || v.def?.label || v.rule.rule_type}
            </strong>
            <span style={{ color: C.muted }}> — {v.result?.reason}</span>
          </li>
        ))}
      </ul>

      <div>
        <label style={{
          display: "block",
          fontSize: 11,
          color: C.muted,
          marginBottom: 6,
          fontWeight: 600,
          letterSpacing: "0.04em",
          textTransform: "uppercase",
        }}>
          What were you thinking?
          <span style={{
            color: C.muted,
            fontWeight: 400,
            textTransform: "none",
            letterSpacing: 0,
            marginLeft: 6,
          }}>
            (optional — just for your own retrospective)
          </span>
        </label>
        <textarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="FOMO, gut feeling, saw a setup I couldn't pass up…"
          rows={2}
          style={{
            width: "100%",
            padding: "10px 12px",
            background: "#0b0d19",
            border: `1px solid ${C.border}`,
            borderRadius: 6,
            color: C.text,
            fontSize: 13,
            fontFamily: "inherit",
            resize: "vertical",
            boxSizing: "border-box",
          }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TRADE REVIEW - ROBUST + SMART AI COACHING
// ─────────────────────────────────────────────────────────────
